<template>
  <app-modal
    v-model="isDialogOpened"
    fullscreen
    content-class="nda-dialog"
    @cancel="closeDialog"
  >
    <template #title>
      <span class="text--medium text--bold">
        {{ $t('project.nda.dialogs.NdaDialog.title') }}
      </span>
    </template>

    <template #body>
      <v-stepper-window v-model="currentStep">
        <v-stepper-window-item :value="1">
          <div class="mx-auto" style="width: 60%">
            <p class="mb-6 text--small text--bold">
              {{ $t('project.nda.dialogs.NdaDialog.inputFileLabel') }}
            </p>
            <p class="text--small">
              {{ $t('project.nda.dialogs.NdaDialog.inputFileDescription') }}
            </p>

            <div class="mt-5">
              <!-- File name -->
              <div v-if="fileToPost" class="imported-file w-100">
                <ClosdFildersIcon :document="fileToPost" />
                <div style="width: 90%;">
                  <p
                    class="text-truncated text--small text--regular"
                  >
                    {{ fileToPost.name }}
                  </p>
                  <app-filesize-display :value="fileToPost.size" />
                </div>
                <app-button type="icon" class="ml-auto" @click="fileToPost = null">
                  <app-material-icon name="close" />
                </app-button>
              </div>

              <!-- Dropzone -->
              <div v-else class="file-dropzone">
                <AppDroppableArea class="file-dropzone__droppable-area pa-12"
                                  @ondrop="dropFiles"
                >
                  <input ref="ndaFile"
                         type="file"
                         class="d-none"
                         :accept="ACCEPTED_DOCUMENT_TYPES"
                         @change="importFiles($event)"
                  >
                  <app-material-icon name="upload" color="inherit" size="lg" />
                  <p class="mt-4 mb-0 px-4 font-weight-medium">
                    {{ $t('project.nda.dialogs.NdaDialog.lblExternalFilesDrop') }}
                  </p>
                  <p class="mt-4 px-4 font-weight-semi-bold text-uppercase">
                    {{ $t('project.nda.dialogs.NdaDialog.lblExternalFilesDropOr') }}
                  </p>
                  <app-button class="mt-4"
                              type="outlined"
                              variant="neutral"
                              style="background-color: inherit;"
                              @click="onSelectFileButtonClick"
                  >
                    {{ $t('project.nda.dialogs.NdaDialog.importFromComputer') }}
                  </app-button>
                </AppDroppableArea>
              </div>
            </div>
          </div>
        </v-stepper-window-item>

        <v-stepper-window-item :value="2">
          <p class="mb-6 text--small">
            {{ $t('project.nda.dialogs.NdaDialog.prepareSignatureIntro') }}
          </p>

          <app-alert-in-page v-if="hasError"
                             :dismissible="false"
                             variant="error"
                             no-icon
          >
            {{ $t('project.nda.dialogs.NdaDialog.addNdaDocumentError') }}
          </app-alert-in-page>
          <PrepareSignatureNda v-else-if="ndaEnvelopeId" :nda-envelope-id="ndaEnvelopeId" />
        </v-stepper-window-item>
      </v-stepper-window>
    </template>

    <template #footer>
      <app-button
        v-if="currentStep > 1"
        type="outlined"
        variant="neutral"
        @click="currentStep--"
      >
        {{$t('common.previous')}}
      </app-button>
      <v-spacer />
      <app-button
        type="outlined"
        variant="neutral"
        :disabled="isOkLoading"
        @click="onCancelAddNda"
      >
        {{ $t('common.cancel') }}
      </app-button>
      <app-button
        class="ml-4"
        :disabled="isOkDisabled || isOkLoading"
        :loading="isOkLoading"
        @click="onOk"
      >
        {{ okText }}
      </app-button>
    </template>
  </app-modal>
</template>

<script>
import { getFilesFromDataTransferItems } from 'datatransfer-files-promise'
import { defineComponent } from 'vue'
import { mapState, mapActions, mapGetters } from 'vuex'

import AppDroppableArea from '@/common/AppDroppableArea.vue'
import { ACCEPTED_DOCUMENT_TYPES } from '@/common/constants'
import ClosdFildersIcon from '@/common/filders/ClosdFildersIcon.vue'
import { tagsTypes } from '@/common/prepare-signature/tagsTypes'
import { isFileSignable } from '@/common/utils/files'
import { toast } from '@/common/utils/toast'
import { dialogMixin } from '@/mixins/dialogMixin'
import PrepareSignatureNda from '@/project/nda/PrepareSignatureNda.vue'
import ndaService from '@/services/nda.service'
import { RESET_DOCUMENT_TO_PREPARE } from '@/store/modules/prepareSignature/action_types'

export default defineComponent({
  name: 'NdaDialog',
  components: {
    AppDroppableArea,
    ClosdFildersIcon,
    PrepareSignatureNda,
  },
  mixins: [dialogMixin],
  props: {
    mnemo: {
      type: String,
      required: true,
    },
  },
  data () {
    return {
      fileToPost: null,
      ndaEnvelopeId: null,
      currentStep: 1,
      file: null,
      addNdaDocumentLoading: false,
      addNdaDocumentError: null,
      validateNdaDocumentLoading: false,
      ACCEPTED_DOCUMENT_TYPES,
    }
  },
  computed: {
    ...mapState('room', ['currentRoom']),
    ...mapState('prepareSignature', [
      'envelopeDisplayInformation',
      'envelopeDisplayInformationError',
      'taggingInformation',
    ]),
    ...mapGetters('room', ['isCurrentUserPm', 'maxSignableSize']),
    isOkDisabled () {
      if (this.currentStep === 1) {
        return !this.fileToPost
      } else {
        return !this.ndaEnvelopeId || !this.signatureSignTags.length || this.hasError
      }
    },
    signatureSignTags () {
      if (!this.taggingInformation?.tags) return []
      return this.taggingInformation.tags.filter(tag => tag.type === tagsTypes.SIGN)
    },
    isOkLoading () {
      return this.addNdaDocumentLoading || this.validateNdaDocumentLoading
    },
    hasError () {
      return this.addNdaDocumentError || this.envelopeDisplayInformationError
    },
    okText () {
      return this.currentStep === 1 ? this.$t('common.next') : this.$t('common.register')
    },
  },
  methods: {
    ...mapActions('prepareSignature', [RESET_DOCUMENT_TO_PREPARE]),
    onSelectFileButtonClick () {
      this.$refs.ndaFile.click()
    },
    importFiles ($event) {
      const { files } = $event.target
      this.fileToPost = this.getFileToPost(files[0])
      // Reset the document to prepare if the user changes the file
      if (this.envelopeDisplayInformation) {
        this.RESET_DOCUMENT_TO_PREPARE()
      }
    },
    async dropFiles (event) {
      const dataTransferItems = event.dataTransfer?.items
      if (dataTransferItems) {
        const droppedFiles = await getFilesFromDataTransferItems(dataTransferItems)
        this.fileToPost = this.getFileToPost(droppedFiles[0])
        // Reset the document to prepare if the user changes the file
        if (this.envelopeDisplayInformation) {
          this.RESET_DOCUMENT_TO_PREPARE()
        }
      }
    },
    getFileToPost (file) {
      const invalidFile = !isFileSignable(file, this.maxSignableSize)
      if (invalidFile) {
        toast.error(this.$t('project.nda.dialogs.NdaDialog.invalidFile'))
        return
      }
      return file
    },
    async onOk () {
      if (this.currentStep === 1) {
        await this.addToPrepareSignatureTemplate()
        this.currentStep++
      } else {
        this.validateNdaDocument()
      }
    },
    async addToPrepareSignatureTemplate () {
      if (!this.fileToPost) return
      this.addNdaDocumentLoading = true
      this.addNdaDocumentError = null
      const formData = new FormData()
      formData.append('fileSelect', this.fileToPost, this.fileToPost.name)

      try {
        const result = await ndaService.addNdaDocument(this.mnemo, formData)
        if (result?.data?.envelopeNda) {
          this.ndaEnvelopeId = result.data.envelopeNda.id
        }
      } catch (error) {
        console.error(error)
        this.addNdaDocumentError = error
        if (error.response?.data?.errorCode === 'ERR_ENVELOPE_ALREADY_EXISTS') {
          toast.error(this.$t('project.nda.dialogs.NdaDialog.addNdaDocumentErrorAlreadyExists'))
        } else {
          toast.error(this.$t('project.nda.dialogs.NdaDialog.addNdaDocumentError'))
        }
      } finally {
        this.addNdaDocumentLoading = false
      }
    },
    async validateNdaDocument () {
      this.validateNdaDocumentLoading = true
      try {
        await ndaService.validateNdaDocument(this.mnemo)
        this.currentRoom.hasNda = true
        this.$emit('nda-added')
        this.closeDialog()
      } catch (error) {
        console.error(error)
        if (error.response?.data?.errorCode === 'ERR_ENVELOPE_MISSING_TO_SEND') {
          toast.error(this.$t('project.nda.dialogs.NdaDialog.validateNdaDocumentErrorMissingTag'))
        } else {
          toast.error(this.$t('common.msgFailErrorOccurred'))
        }
      } finally {
        this.validateNdaDocumentLoading = false
      }
    },
    onCancelAddNda () {
      this.closeDialog()
    },
  },
})
</script>

<style lang="scss">
@import "@/scss/border-tokens";

.nda-dialog {
  & > .v-card {
    flex: 1;
  }

  .file-dropzone {
    @include border-dashed(rgb(var(--v-theme-border-secondary)));
    background: rgb(var(--v-theme-surface-tertiary));
    @include border-radius('border-radius-3');

    &__droppable-area {
      height: 100%;
      display: flex;
      flex-direction: column;
      align-items: center;
    }

    .drag-in-progress {
      background-color: #F0F6FF;
      outline: 2px dashed #2671F2;
    }
  }
  .imported-file {
    display: inline-flex;
    align-items: center;
    padding: 4px 16px;
    @include border-radius('border-radius-2');
    border: get-border('border-secondary');
    background: rgb(var(--v-theme-surface-tertiary));
  }
}
</style>
